import type {
  BookingStatus,
  ProviderBooking,
  ProviderBookingsResponse,
} from "../../lib/api";
import { formatDateTimeForPicker } from "./booking-utils";

export interface CachedBookingFilters {
  scheduledFromLocal: string | null;
  scheduledToLocal: string | null;
  statuses: BookingStatus[];
  unitId: string | null;
  venueId: string | null;
}

export function isBookingsCachePath(path: string) {
  return path.startsWith("/api/v1/provider/bookings");
}

export function parseBookingsCachePath(path: string): CachedBookingFilters | null {
  if (!isBookingsCachePath(path)) {
    return null;
  }

  const url = new URL(path, "http://provider.local");
  const rawStatuses = url.searchParams.get("statuses");

  return {
    venueId: url.searchParams.get("venue_id"),
    unitId: url.searchParams.get("unit_id"),
    statuses: rawStatuses
      ? (rawStatuses
          .split(",")
          .map((value) => value.trim())
          .filter(Boolean) as BookingStatus[])
      : [],
    scheduledFromLocal: url.searchParams.get("scheduled_from_local"),
    scheduledToLocal: url.searchParams.get("scheduled_to_local"),
  };
}

export function sortBookings(bookings: ProviderBooking[]) {
  return [...bookings].sort((left, right) =>
    left.scheduled_start_at.localeCompare(right.scheduled_start_at),
  );
}

export function isBookingVisibleInFilters(
  booking: ProviderBooking,
  filters: CachedBookingFilters,
) {
  if (filters.venueId && booking.venue_id !== filters.venueId) {
    return false;
  }

  if (filters.unitId && booking.unit_id !== filters.unitId) {
    return false;
  }

  if (filters.statuses.length > 0 && !filters.statuses.includes(booking.status)) {
    return false;
  }

  const startLocal = formatDateTimeForPicker(
    booking.scheduled_start_at,
    booking.venue_timezone,
  );
  const endLocal = formatDateTimeForPicker(
    booking.scheduled_end_at,
    booking.venue_timezone,
  );

  if (filters.scheduledFromLocal && endLocal < filters.scheduledFromLocal) {
    return false;
  }

  if (filters.scheduledToLocal && startLocal > filters.scheduledToLocal) {
    return false;
  }

  return true;
}

export function isProviderBookingsResponse(value: unknown): value is ProviderBookingsResponse {
  return (
    value !== null &&
    typeof value === "object" &&
    "bookings" in value &&
    Array.isArray((value as ProviderBookingsResponse).bookings)
  );
}

export function mergeBookingIntoCachedResponse(
  path: string,
  value: unknown,
  booking: ProviderBooking,
) {
  if (!isProviderBookingsResponse(value)) {
    return value;
  }

  const response = value;

  const filters = parseBookingsCachePath(path);
  if (!filters) {
    return value;
  }

  const nextBookings = response.bookings.filter((entry) => entry.id !== booking.id);
  if (isBookingVisibleInFilters(booking, filters)) {
    nextBookings.push(booking);
  }

  return {
    ...response,
    bookings: sortBookings(nextBookings),
  } satisfies ProviderBookingsResponse;
}

export function findCachedBookingById(
  cachedEntries: Record<string, { value: unknown }>,
  bookingId: string,
) {
  for (const entry of Object.values(cachedEntries)) {
    if (!isProviderBookingsResponse(entry.value)) {
      continue;
    }

    const booking = entry.value.bookings.find((candidate) => candidate.id === bookingId);
    if (booking) {
      return booking;
    }
  }

  return null;
}

export function listCachedBookings(
  cachedEntries: Record<string, { value: unknown }>,
) {
  const bookingsById = new Map<string, ProviderBooking>();

  for (const entry of Object.values(cachedEntries)) {
    if (!isProviderBookingsResponse(entry.value)) {
      continue;
    }

    for (const booking of entry.value.bookings) {
      bookingsById.set(booking.id, booking);
    }
  }

  return [...bookingsById.values()];
}
